import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Title,
  Button,
  SegmentedControl,
  Loader,
  Card,
  Badge,
  Center,
  Divider,
  Box,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { MapPin, CheckCircle2, Footprints, X } from "lucide-react";
import { AmenityRow, type AmenityItem } from "@nodus/ui";
import { WayfindingPanel, type WayfindData } from "./WayfindingPanel";
import { RoomPriceBadge } from "./RoomPriceBadge";
import { hasPriceInfo, type RoomPricingLike } from "./pricing";

export interface FreeRoomCandidate {
  id: number;
  name: string;
  /** Underlying Room.name (e.g. "22606"), shown as a subtitle when
   *  multiple rooms share the same display name. */
  real_name?: string;
  description?: string | null;
  floor_name: string;
  address_name: string;
  seats?: number | null;
  has_camera?: boolean;
  has_tv?: boolean;
  has_speaker?: boolean;
  next_free_slot: { start: string; duration_minutes: number };
  free_for_minutes: number;
  is_same_floor: boolean;
  distance_meters?: number | null;
  same_floorplan?: boolean;
  floorplan_id?: number | null;
  /** Optional room pricing. A paid room (or one whose price starts later) shows a
   *  RoomPriceBadge (e.g. "500 kr/h excl. VAT") under the room name so a member never
   *  books a paid room without seeing its price. Absent → no price line. */
  pricing?: RoomPricingLike | null;
}

export interface FindFreeRoomModalProps {
  opened: boolean;
  onClose: () => void;
  /** Caller supplies the data fetcher — uses tenant JWT or device JWT
   *  via its own axios instance. */
  fetchFreeRooms: (durationMinutes: number) => Promise<FreeRoomCandidate[]>;
  /** Caller supplies the booking call. The modal already knows the
   *  candidate's name + floor (from the list it just rendered), so the
   *  caller only needs to return the booking's `end` time so the
   *  confirmation can show "ends at HH:MM". */
  bookRoom: (
    roomId: number,
    durationMinutes: number,
  ) => Promise<{ end: string }>;
  /** Quick-book buttons. Defaults to 15/30/45/60 to match the kiosk. */
  durations?: number[];
  /** Heading shown at the top of the modal. */
  title?: string;
  /** Optional wayfinding fetcher. When supplied, selecting a row reveals
   *  a side-panel preview of the route. Caller passes its own JWT-aware
   *  axios. */
  fetchWayfinding?: (roomId: number) => Promise<WayfindData>;
  /** When supplied, the wayfind preview uses a server-pre-rendered SVG
   *  via `<img src={builder(roomId)}>` instead of building the SVG
   *  client-side from `fetchWayfinding`. Recommended for kiosk-class
   *  devices where parsing the floorplan body and reconciling React
   *  markers per tap is too expensive. The two callbacks are
   *  independent — provide either one or both (svg wins when both
   *  present). */
  buildWayfindSvgUrl?: (roomId: number) => string | null;
  /** Reports caught errors back to the host app so it can capture to
   *  Sentry / log. The modal still renders a retry control regardless. */
  onError?: (err: unknown, context: "fetch_rooms" | "wayfind" | "book") => void;
  /** Touchscreen mode: enlarges the header, close button, duration
   *  picker, and per-room booking buttons so they're comfortable to tap
   *  on a wall-mounted kiosk panel. Mouse/web callers (the tenant portal)
   *  leave this off and get the compact default sizing. */
  touch?: boolean;
}

const DEFAULT_DURATIONS = [15, 30, 45, 60];

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });

// Maps a FreeRoomCandidate's flat booleans onto amenity items. The mapping
// is this component's job — ``@nodus/ui`` draws amenities, it knows nothing
// about the booking domain — so only the drawing is delegated.
//
// The row is label-bearing because the seat *count* has to show, and
// ``showLabel`` is a row-wide switch: the seat count rides in ``label`` (so it
// reads "6", not "6 seats") and the equipment icons pass an empty label to
// stay icon-only, matching how this list has always read.
const AmenityIcons: React.FC<{ c: FreeRoomCandidate; touch?: boolean }> = ({ c, touch }) => {
  const items: AmenityItem[] = [];
  if (c.seats != null) items.push({ type: "seat", label: String(c.seats) });
  if (c.has_camera) items.push({ type: "camera", label: "" });
  if (c.has_tv) items.push({ type: "tv", label: "" });
  if (c.has_speaker) items.push({ type: "speaker", label: "" });
  // ``labelSize`` matters only on the kiosk: the seat count is the one label
  // here, and touch mode exists so a wall-mounted panel is readable from across
  // a room. Leaving it at the default 12px would shrink the number that mode is
  // meant to enlarge.
  return (
    <AmenityRow
      items={items}
      useColor={false}
      size={touch ? 20 : 14}
      labelSize={touch ? "md" : "xs"}
    />
  );
};

export const FindFreeRoomModal: React.FC<FindFreeRoomModalProps> = ({
  opened,
  onClose,
  fetchFreeRooms,
  bookRoom,
  durations = DEFAULT_DURATIONS,
  title = "Find a free meeting room",
  fetchWayfinding,
  buildWayfindSvgUrl,
  onError,
  touch = false,
}) => {
  const [searchDuration, setSearchDuration] = useState<number>(durations[0]);
  const [rooms, setRooms] = useState<FreeRoomCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [bookingRoomId, setBookingRoomId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
  const [wayfindData, setWayfindData] = useState<WayfindData | null>(null);
  const [wayfindLoading, setWayfindLoading] = useState(false);
  const [wayfindError, setWayfindError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<{
    roomId: number;
    room_name: string;
    floor_name: string;
    end: string;
    wayfind?: WayfindData | null;
  } | null>(null);

  // Load on open + duration change.
  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchFreeRooms(searchDuration)
      .then((data) => {
        if (!cancelled) {
          // De-dupe by id (defensive — backend should already de-dupe).
          const seen = new Set<number>();
          const unique = data.filter((c) => {
            if (seen.has(c.id)) return false;
            seen.add(c.id);
            return true;
          });
          setRooms(unique);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message ?? "Couldn't load rooms");
        onError?.(err, "fetch_rooms");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [opened, searchDuration, fetchFreeRooms]);

  // Reset on close.
  useEffect(() => {
    if (!opened) {
      setConfirmation(null);
      setError(null);
      setBookingRoomId(null);
      setSelectedRoomId(null);
      setWayfindData(null);
      setWayfindError(null);
    }
  }, [opened]);

  // Load wayfinding for the selected row.
  useEffect(() => {
    if (!fetchWayfinding || selectedRoomId == null) {
      setWayfindData(null);
      setWayfindError(null);
      return;
    }
    let cancelled = false;
    setWayfindLoading(true);
    setWayfindError(null);
    fetchWayfinding(selectedRoomId)
      .then((d) => {
        if (!cancelled) setWayfindData(d);
      })
      .catch((err) => {
        if (!cancelled) {
          setWayfindData(null);
          setWayfindError(err?.message ?? "Couldn't load route");
        }
        onError?.(err, "wayfind");
      })
      .finally(() => {
        if (!cancelled) setWayfindLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRoomId, fetchWayfinding, onError]);

  const handleBook = async (candidate: FreeRoomCandidate, minutes: number) => {
    setBookingRoomId(candidate.id);
    setError(null);
    try {
      const result = await bookRoom(candidate.id, minutes);
      // Best-effort wayfinding fetch for the confirmation panel — the
      // booked room is the obvious target. Use the data we already have
      // in state when it matches; otherwise refetch so the confirmation
      // shows a route even if the user never tapped the row first.
      let wayfind: WayfindData | null = null;
      if (fetchWayfinding) {
        if (selectedRoomId === candidate.id && wayfindData) {
          wayfind = wayfindData;
        } else {
          try {
            wayfind = await fetchWayfinding(candidate.id);
          } catch (wayfindErr) {
            // Booking already succeeded; degrade the confirmation map
            // gracefully but report the error so we know the wayfind
            // endpoint is broken in the field.
            wayfind = null;
            onError?.(wayfindErr, "wayfind");
          }
        }
      }
      setConfirmation({
        roomId: candidate.id,
        room_name: candidate.name,
        floor_name: candidate.floor_name,
        end: result.end,
        wayfind,
      });
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? err?.message ?? "Booking failed");
      onError?.(err, "book");
    } finally {
      setBookingRoomId(null);
    }
  };

  const durationData = useMemo(
    () => durations.map((m) => ({ value: String(m), label: `${m} min` })),
    [durations],
  );

  const showWayfindPanel = Boolean(fetchWayfinding) || Boolean(buildWayfindSvgUrl);
  // Portrait kiosks (IAdea panels mounted vertically) get a stacked
  // layout — full-screen modal with rooms on top, wayfind on the
  // bottom half — instead of the landscape side-by-side split.
  const isPortrait = useMediaQuery("(orientation: portrait)") ?? false;

  // The selected wayfind side/bottom panel — extracted so both the
  // landscape sticky-right layout and the portrait stacked layout
  // can render the same placeholder/error/panel states.
  const renderWayfindPanel = () => {
    if (selectedRoomId == null) {
      return (
        <Card withBorder padding="md" h="100%">
          <Stack gap={4} align="center" justify="center" h="100%">
            <MapPin size={touch ? 32 : 24} />
            <Text fz={touch ? "lg" : "sm"} fw={500}>Tap a room to preview the route</Text>
            <Text fz={touch ? "md" : "xs"} c="dimmed" ta="center">
              See the floorplan + walking direction before you book.
            </Text>
          </Stack>
        </Card>
      );
    }
    if (wayfindError) {
      return (
        <Card withBorder padding="sm" h="100%">
          <Stack gap="xs" align="center" py="md">
            <Text fz="sm" c="red" ta="center">
              Couldn't load route: {wayfindError}
            </Text>
            <Button
              size="xs"
              variant="light"
              onClick={() => {
                // Re-trigger the load effect by re-setting the id.
                const id = selectedRoomId;
                setSelectedRoomId(null);
                setTimeout(() => setSelectedRoomId(id), 0);
              }}
            >
              Retry
            </Button>
          </Stack>
        </Card>
      );
    }
    return (
      <Card withBorder padding="sm" h="100%">
        <WayfindingPanel
          data={wayfindData}
          loading={wayfindLoading}
          height={isPortrait ? 700 : 300}
          svgUrl={
            buildWayfindSvgUrl && selectedRoomId
              ? buildWayfindSvgUrl(selectedRoomId)
              : null
          }
        />
        {wayfindData?.same_floorplan === false && wayfindData?.floorplan && (
          <>
            <Divider my="xs" />
            <Text fz="xs" c="dimmed">
              Origin is on a different floorplan — straight-line preview
              only.
            </Text>
          </>
        )}
      </Card>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        touch ? (
          <Title order={2} fz={32} fw={700}>
            {title}
          </Title>
        ) : (
          title
        )
      }
      size={isPortrait ? undefined : showWayfindPanel ? "1100px" : "lg"}
      fullScreen={isPortrait}
      centered
      // Kiosk panels are tapped, not clicked — give the dismiss control a
      // finger-sized hit box with a bold glyph instead of the 28px default.
      closeButtonProps={
        touch
          ? { size: 56, icon: <X size={32} strokeWidth={2.5} />, "aria-label": "Close" }
          : undefined
      }
      // Old kiosk hardware (Android 7 IAdea panels) janks on the
      // open/close scale+fade transition. Render the modal instantly
      // there; mouse/web callers keep the default animation.
      transitionProps={touch ? { duration: 0 } : undefined}
    >
      {confirmation ? (
        <Stack gap="md" align="center" py="md">
          <CheckCircle2 size={touch ? 88 : 64} color="#34d399" />
          <Title order={touch ? 2 : 3} ta="center">Booked!</Title>
          <Text fz={touch ? 28 : "lg"} ta="center">
            Walk to <Text component="span" fw={700}>{confirmation.room_name}</Text>
          </Text>
          <Text c="dimmed" ta="center" fz={touch ? "lg" : undefined}>
            on {confirmation.floor_name} · ends at {formatTime(confirmation.end)}
          </Text>
          {(confirmation.wayfind || (buildWayfindSvgUrl && confirmation.roomId)) && (
            <Box w="100%" maw={720}>
              <WayfindingPanel
                data={confirmation.wayfind ?? null}
                height={280}
                showCaption={false}
                svgUrl={
                  buildWayfindSvgUrl && confirmation.roomId
                    ? buildWayfindSvgUrl(confirmation.roomId)
                    : null
                }
              />
            </Box>
          )}
          <Button mt="sm" size={touch ? "xl" : undefined} onClick={onClose}>Done</Button>
        </Stack>
      ) : (
        <Stack gap="md">
          <SegmentedControl
            value={String(searchDuration)}
            onChange={(v: string) => setSearchDuration(Number(v))}
            data={durationData}
            fullWidth
            size={touch ? "xl" : "sm"}
          />

          {loading && (
            <Center py="xl">
              <Loader size={touch ? "xl" : undefined} />
            </Center>
          )}

          {!loading && error && (
            <Text c="red" ta="center" fw={touch ? 600 : undefined} fz={touch ? 30 : undefined}>
              {error}
            </Text>
          )}

          {!loading && !error && rooms.length === 0 && (
            <Text c="dimmed" ta="center" py="xl" fw={touch ? 600 : undefined} fz={touch ? 30 : undefined}>
              No rooms free for {searchDuration} minutes right now.
            </Text>
          )}

          {!loading && rooms.length > 0 && showWayfindPanel && !isPortrait && (
            <Group align="flex-start" wrap="nowrap" gap="md">
              <Box flex={1} miw={0}>
                <RoomList />
              </Box>
              <Box w={420} style={{ position: "sticky", top: 0 }}>
                {renderWayfindPanel()}
              </Box>
            </Group>
          )}

          {!loading && rooms.length > 0 && showWayfindPanel && isPortrait && (
            <Stack gap="md" h="calc(100vh - 180px)">
              {/* Top half: scrollable list of room cards. */}
              <Box flex={1} miw={0} style={{ overflowY: "auto", minHeight: 0 }}>
                <RoomList />
              </Box>
              {/* Bottom half: floorplan / wayfind preview. */}
              <Box flex={1} miw={0} style={{ minHeight: 0 }}>
                {renderWayfindPanel()}
              </Box>
            </Stack>
          )}

          {!loading && rooms.length > 0 && !showWayfindPanel && <RoomList />}
        </Stack>
      )}
    </Modal>
  );

  function RoomList() {
    // Names that occur more than once need a floor suffix to
    // disambiguate (e.g. "Ping Pong" exists on three floors).
    const nameCounts = new Map<string, number>();
    rooms.forEach((c) => {
      nameCounts.set(c.name, (nameCounts.get(c.name) ?? 0) + 1);
    });
    return (
      <Stack gap="xs">
        {rooms.map((c) => {
          const isBookingThis = bookingRoomId === c.id;
          const ambiguous = (nameCounts.get(c.name) ?? 0) > 1;
          const showRealName = c.real_name && c.real_name !== c.name;
          const isSelected = selectedRoomId === c.id;
          return (
            <Card
              key={c.id}
              withBorder
              padding="sm"
              style={{
                cursor: showWayfindPanel ? "pointer" : "default",
                borderColor: isSelected ? "var(--mantine-color-blue-6)" : undefined,
                borderWidth: isSelected ? 2 : 1,
              }}
              onClick={showWayfindPanel ? () => setSelectedRoomId(c.id) : undefined}
            >
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={2} flex={1}>
                  <Group gap="xs">
                    <Text fz={touch ? "xl" : "md"} fw={600}>{c.name}</Text>
                    {ambiguous && showRealName && (
                      <Text fz={touch ? "sm" : "xs"} c="dimmed">#{c.real_name}</Text>
                    )}
                    {c.is_same_floor && (
                      <Badge size={touch ? "lg" : "xs"} color="blue">Same floor</Badge>
                    )}
                    {c.distance_meters != null && (
                      <Group gap={2}>
                        <Footprints size={touch ? 16 : 12} />
                        <Text fz={touch ? "sm" : "xs"} c="dimmed">
                          {Math.round(c.distance_meters)} m
                        </Text>
                      </Group>
                    )}
                  </Group>
                  {c.description && c.description !== c.name && (
                    <Text fz={touch ? "md" : "xs"}>{c.description}</Text>
                  )}
                  {hasPriceInfo(c.pricing) && (
                    <div>
                      <RoomPriceBadge pricing={c.pricing} size={touch ? "md" : "sm"} />
                    </div>
                  )}
                  <Group gap={4}>
                    <MapPin size={touch ? 16 : 12} />
                    <Text fz={touch ? "md" : "xs"} c="dimmed">
                      {c.address_name} · {c.floor_name}
                    </Text>
                  </Group>
                  <Group gap="md" mt={4}>
                    <AmenityIcons c={c} touch={touch} />
                    <Text fz={touch ? "md" : "xs"} c="dimmed">
                      Free for {c.free_for_minutes} min
                    </Text>
                  </Group>
                </Stack>
                <Stack gap={4} align="flex-end">
                  <Group gap={4}>
                    {durations
                      .filter((m) => m <= c.free_for_minutes)
                      .map((m) => (
                        <Button
                          key={m}
                          size={touch ? "lg" : "xs"}
                          variant={m === searchDuration ? "filled" : "light"}
                          color="green"
                          loading={isBookingThis}
                          disabled={bookingRoomId !== null && !isBookingThis}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            handleBook(c, m);
                          }}
                        >
                          {m}m
                        </Button>
                      ))}
                  </Group>
                </Stack>
              </Group>
            </Card>
          );
        })}
      </Stack>
    );
  }
};
